import React, { useMemo } from 'react';
import { Tooltip } from '@mui/material';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import usePlanLock from '../../../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../../../consts/SubscriptionFeatures';
import CustomButton from '../../../../components/buttons/CustomButton';
import HiddenReport from '../../../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../../../components/documents-templates/report/useReportExport';
import FullReportTemplate from '../../../../components/documents-templates/report/FullReportTemplate';
import { listAvailableSections, prepareFullReport } from '../../modules/reportSections';
import { diagnosesOfAppointment } from '../../modules/diagnoses/diagnoses';

const EMPTY_HINT = 'Nenhum módulo deste atendimento tem registro para exportar';

/**
 * Botão "Exportar relatório completo": um único PDF com todos os módulos que
 * têm registro, na ordem dos cards. Fica desabilitado, com a explicação no
 * tooltip, quando nenhum módulo tem o que exportar.
 *
 * `modules`: chaves visíveis (resolveVisibleModules); `ctx`: contexto das
 * seções (ver reportSections.js); `title`/`subtitle`/`date`: capa do relatório.
 *
 * Cadeado por plano (clinical_reports): o botão continua visível, com cadeado,
 * e o clique abre o modal de upgrade em vez de gerar o PDF.
 */
const FullReportButton = ({ modules, ctx, patientName, professionalId, companyConfig, title, subtitle, date }) => {
  const available = useMemo(() => listAvailableSections(modules, ctx), [modules, ctx]);
  const lock = usePlanLock(SUBSCRIPTION_PERMISSIONS.CLINICAL_REPORTS, 'O relatório completo do atendimento');

  const report = useReportExport({
    patientName,
    professionalId,
    companyConfig,
    reportType: 'relatorio-completo',
    prepare: async () => {
      const sections = await prepareFullReport(modules, ctx);
      const diagnoses = diagnosesOfAppointment(ctx.data && ctx.data.diagnoses, ctx.appointment && ctx.appointment.id);
      return sections ? { sections, diagnoses } : null;
    },
  });

  const button = (
    <CustomButton
      variant="outlined"
      style="secondary"
      label={report.exporting ? 'Gerando relatório...' : 'Exportar relatório completo'}
      icon={lock.locked ? <LockOutlinedIcon /> : <PictureAsPdfOutlinedIcon />}
      enabled={lock.locked || (available.length > 0 && !report.exporting)}
      onClick={lock.guard(report.start)}
    />
  );

  return (
    <>
      {available.length === 0 && !lock.locked ? (
        <Tooltip title={EMPTY_HINT}><span>{button}</span></Tooltip>
      ) : button}
      {lock.modal}

      {report.job && (
        <HiddenReport>
          <FullReportTemplate
            ref={report.ref}
            {...report.paperProps}
            title={title}
            subtitle={subtitle}
            date={date}
            sections={report.job.sections}
            diagnoses={report.job.diagnoses}
          />
        </HiddenReport>
      )}
    </>
  );
};

export default FullReportButton;
